import { useMemo, useCallback } from "react";
import { ListInstance } from "@/types/list";

export interface ColumnToggleItem {
	id: string;
	label: string;
	visible: boolean;
}

export interface ColumnGroup {
	label: string;
	children: ColumnToggleItem[];
}

export const useColumnGroups = <TData>(table: ListInstance<TData>) => {
	const updateVisibility = useCallback(
		(id: string, visible: boolean) => {
			const current = table.getState().columnVisibility;
			table.setColumnVisibility({ ...current, [id]: visible });
		},
		[table],
	);

	const setManyVisibilities = useCallback(
		(newVisibilities: Record<string, boolean>) => {
			const current = table.getState().columnVisibility;
			table.setColumnVisibility({ ...current, ...newVisibilities });
		},
		[table],
	);

	const createColumnItem = useCallback(
		(col: any): ColumnToggleItem => ({
			id: col.id,
			label: String(col.columnDef.header),
			visible: col.getIsVisible(),
		}),
		[],
	);

	const columnGroups: ColumnGroup[] = useMemo(() => {
		const allColumns = table.getAllColumns();
		const hasGroups = allColumns.some(col => Array.isArray(col.columns) && col.columns.length > 0);

		if (hasGroups) {
			return allColumns
				.filter(group => group.columns?.length)
				.map(group => ({
					label: String(group.columnDef.header),
					children: group.columns.filter(col => col.getCanHide()).map(createColumnItem),
				}))
				.filter(group => group.children.length > 0);
		}

		return [
			{
				label: "Columns",
				children: table
					.getAllLeafColumns()
					.filter(col => col.getCanHide())
					.map(createColumnItem),
			},
		];
	}, [table, table.getAllColumns(), table.getAllLeafColumns(), table.getState().columnVisibility, createColumnItem]);

	const totalVisible = useMemo(
		() => table.getAllLeafColumns().filter(col => col.getIsVisible()).length,
		[table, table.getAllLeafColumns(), table.getState().columnVisibility],
	);

	return {
		columnGroups,
		totalVisible,
		updateVisibility,
		setManyVisibilities,
	};
};
